import React, { useEffect, useMemo, useState } from 'react';
import { navigate } from 'gatsby-link';
import { ComparisonTableContentfulModel } from 'src/api/models/ComparisonTable';
import DynamicComponents from 'src/functions/dynamicComponents';
import { layout } from 'app_previous/styles/layout';
import styled, { css, CSSProperties } from 'styled-components';
import { getStyleObjectFromString } from 'src/functions/getStyleObjectFromString';
import PolicyLink from 'app_previous/elements/links/Link.policies';
import { propagateQuerytoExternalLinks, urlIsExternal, reApplyTrackingToUrl } from './propagateQueryParams';
import { windowIsLoaded } from 'src/functions/windowIsLoaded';
import TableOfContentContainer from 'app_previous/organisms/Standard/Shared/Tables/TableOfContent';
import { getValueRecursive, headingToKey } from './getTableOfContentDictionary';
import { isUrlToFreeTrial } from 'app_previous/elements/links/Link.branded';
import { getEmbedInstagramPostHTML } from './getEmbedInstagramPostHTML';
import { LinkPlainExternal } from 'app_previous/elements/links/Link.plain';
import { useArticleTableOfContents } from 'src/hooks/useArticleTableOfContents';
import { palette } from 'app_previous/styles/colors';
import { SeoTableContentfulModel } from 'src/api/models/SEOTable';
import { EmailCaptureFormContentfulModel } from 'src/api/models/EmailCaptureForm';
import { AiNameGeneratorWidgetContentfulModel } from 'src/api/models/AiNameGeneratorWidget';
import SenjaEmbed from 'molecules/SenjaReviews/SenjaEmbed';
import { ColoredTextBoxContentModel } from 'src/api/models/ColoredTextBox';
import { LargeBlockQuoteContentModel } from 'src/api/models/LargeBlockQuote';
import { MemberSpotlightContentModel } from 'src/api/models/MemberSpotlight';
import { ImageContentfulModel } from 'src/api/models/Image';
import useExperimentCtaUrl from 'src/hooks/useExperimentCtaUrl';

export interface MarkupValue {
  type: 'text';
  value: string;
}

export interface MarkupElement {
  type: 'element';
  tagName:
    | 'a'
    | 'p'
    | 'em'
    | 'blockquote'
    | 'h1'
    | 'h2'
    | 'h3'
    | 'h4'
    | 'h5'
    | 'strong'
    | 'ul'
    | 'ol'
    | 'li'
    | 'img'
    | 'table'
    | 'thead'
    | 'tbody'
    | 'tr'
    | 'th'
    | 'td'
    | 'br'
    | 'iframe';
  properties: {
    href?: string;
    src?: string;
    alt?: string;
    style?: any;
    id?: string;
    toc_text?: string;
    toc_ignore?: string;
  };
  children: (MarkupElement | MarkupValue)[];
}

export type Template = 'legalArticle' | 'resource' | 'caseStudy' | 'encyclopedia' | 'caseStudyPro' | 'resourcePro';

const TemplateSpacer = styled.br`
  display: block;
  content: '';
`;

const TemplateBlockQuote = styled.div`
  position: relative;
  padding: 3rem;
  box-sizing: border-box;
  border-radius: 16px;
  border: solid 1px ${palette.black};
  margin-top: 1rem;
  display: grid;
  row-gap: 0.5rem;

  & > p {
    font-size: 1.175rem;
    font-weight: 700;
    line-height: 1.725;
    font-family: Walsheim;
    margin: 0px;
  }
`;

const TemplateBlockQuoteIcon = styled.svg`
  position: relative;
  justify-self: center;
  width: 58px;
  height: 41px;
`;

const TemplateImagePair = styled.div`
  position: relative;
  display: flex;
  flex-flow: row wrap;
  justify-content: space-between;
  align-items: stretch;
  width: 100%;
  left: 0;
  margin: 1rem 0 1.5rem;

  & > img,
  & > div {
    max-width: calc(50% - 18px) !important;
    width: calc(50% - 18px) !important;
    left: 0 !important;
  }
`;

const TemplateImageCovered = styled.div`
  padding-bottom: 28.5%;
  height: 0;
  background-size: cover;
  background-position: 50%;
`;

const LinkedInPostWrapper = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;

  @media screen and (max-width: ${layout.sm}) {
    > iframe {
      max-width: 100%;
    }
  }
`;

const applyGaTrackingOnHtml = (url: string) => (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
  event.preventDefault();
  if (urlIsExternal(url)) {
    // External links to MN.CO,
    // >> Ga needs updating as it depends on timestamp
    const trackedUrl = reApplyTrackingToUrl(url);
    if (!trackedUrl.includes('_ga')) {
      (window as any).TrackJS && (window as any).TrackJS.console.error('_ga missing!', { url: trackedUrl });
      (window as any).TrackJS && (window as any).TrackJS.track(`_ga missing! ${trackedUrl}`);
    }
    window.open(trackedUrl, '_blank').focus();
  } else {
    // internal are defered to Gatsby (internals are faster)
    navigate(url);
  }
};

const renderLinkedInEmbeddedPost = (iframeProps: React.IframeHTMLAttributes<HTMLIFrameElement>) => (
  <LinkedInPostWrapper>
    <iframe
      frameBorder='0'
      {...iframeProps}
    ></iframe>
  </LinkedInPostWrapper>
);

const removeSameDomain = (url: string) => {
  const domainsData = [
    'https://www.mightynetworks.com',
    'https://mightynetworks.com',
    'http://www.mightynetworks.com',
    'http://mightynetworks.com',
  ];

  const indexOfDomain = domainsData.findIndex((domain) => url.startsWith(domain));

  if (!urlIsExternal(url) || indexOfDomain > -1) {
    const toBeReplaced = url.replace(domainsData[indexOfDomain], '');

    return toBeReplaced;
  }

  return url;
};

export const AttachmentContainer = styled.div<{ ignorePadding?: boolean }>`
  position: relative;
  width: calc(100% + 200px);
  left: -100px;
  box-sizing: border-box;

  ${({ ignorePadding }) =>
    !ignorePadding &&
    css`
      padding: 0 1.2rem;
    `}

  > iframe {
    width: 100%;
    height: 510px;
  }

  @media screen and (max-width: ${layout.md}) {
    width: calc(100% + 100px);
    left: -50px;

    > iframe {
      height: 420px;
    }
  }

  @media screen and (max-width: ${layout.subMd}) {
    ${({ ignorePadding }) =>
      !ignorePadding &&
      css`
        padding: 0 1.8rem;
      `}
  }

  @media screen and (max-width: ${layout.xs}) {
    ${({ ignorePadding }) =>
      !ignorePadding &&
      css`
        padding: 0 2.8rem;
      `}

    > iframe {
      height: 210px;
    }
  }
`;

const InstagramPostWrapper = styled.div`
  margin: 35px 0;
`;

export type Attachments = (
  | ComparisonTableContentfulModel
  | SeoTableContentfulModel
  | EmailCaptureFormContentfulModel
  | AiNameGeneratorWidgetContentfulModel
  | ColoredTextBoxContentModel
  | LargeBlockQuoteContentModel
  | MemberSpotlightContentModel
  | ImageContentfulModel
)[];

const mapRecursiveHtmlElement = (
  element: MarkupElement,
  key: string,
  attachmentList: Attachments = null,
  template: Template = null
) =>
  element.children.map((child, index) =>
    formatElementFromHtmlAst({
      element: child,
      key: `${key}-${index}`,
      attachmentList,
      template,
    })
  );

const attachmentsWithoutPadding = [
  'ContentfulEmailCaptureForm',
  'ContentfulAiGeneratorWidget',
  'ContentfulCaseStudyHighlight',
  'ContentfulComparisonTable',
  'ContentfulSeoTable',
  'ContentfulColoredTextBox',
  'ContentfulLargeBlockQuote',
  'ContentfulMemberSpotlight',
  'ContentfulImage',
];

const mapMarkdownAttachments = (chunk: string, key: string, attachmentList: Attachments): React.ReactNode => {
  const attachments = chunk.split(/\(\(([0-9)]+)\)\)/g);

  return attachments.map((attachment) => {
    const attachmentIndex = Number(attachment) - 1;

    if (attachmentList[attachmentIndex]) {
      const content = attachmentList[attachmentIndex];
      const componentRenderer = DynamicComponents[content.internal?.type];

      if (!componentRenderer) {
        return '';
      }

      return (
        <AttachmentContainer
          className='attachment-container'
          key={content.id}
          ignorePadding={attachmentsWithoutPadding.includes(content.internal.type)}
        >
          {componentRenderer(content)}
        </AttachmentContainer>
      );
    }

    return isNaN(attachmentIndex)
      ? formatElementFromHtmlAst({
          element: { type: 'text', value: attachment },
          key,
          attachmentList,
        })
      : '';
  });
};

const mapTableOfContent = (chunk: string, key: string): React.ReactNode => {
  const tocFragments = chunk.split(/\(\(([toc)]+)\)\)/g);

  return tocFragments.map((fragment, i) => {
    if (fragment === 'toc') {
      return <TableOfContentContainer key={`${key}-${i}`} />;
    }

    return formatElementFromHtmlAst({
      element: { type: 'text', value: fragment },
      key: `${key}-${i}`,
    });
  });
};

interface HtmlAstAnchorElementProps {
  url: string;
  children: React.ReactNode;
  className?: string;
  style?: CSSProperties;
}

export const HtmlAstAnchorElement = ({ url, children, className, style }: HtmlAstAnchorElementProps): JSX.Element => {
  const [queryPropagatedUrl, updateHref] = useState(url);
  const experimentCtaUrl = useExperimentCtaUrl();
  const urlToUse = useMemo(() => {
    if (experimentCtaUrl && url.includes('request-a-call-form')) {
      return experimentCtaUrl;
    }

    return url;
  }, [experimentCtaUrl, url]);

  useEffect(() => {
    if (urlToUse) {
      windowIsLoaded().then(() => {
        updateHref(propagateQuerytoExternalLinks(urlToUse));
      });
    }
  }, [urlToUse]);

  const onClick = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    event.preventDefault();

    // Cases where URL is undefined have occurred (MKT-6595)
    const element = url && document.querySelector(url);

    if (!element && typeof window === 'undefined') return;
    window.scrollTo({ top: element.getBoundingClientRect().top + window.pageYOffset - 90, behavior: 'smooth' });
  };

  const targetProp = useMemo(() => {
    if (!url) {
      return {};
    }

    return urlIsExternal(url) ? { target: '_blank', rel: 'noreferrer' } : {};
  }, [url]);

  const navigationProp =
    url.includes('#') && !urlIsExternal(url)
      ? { onClick }
      : {
          href: removeSameDomain(queryPropagatedUrl || '#'),
          onClick: applyGaTrackingOnHtml(removeSameDomain(queryPropagatedUrl || '#')),
        };

  const captionInserter = () => {
    if (isUrlToFreeTrial(url) && className === 'button') {
      return <>{children}</>;
    }

    return children;
  };

  return (
    <LinkPlainExternal
      className={`${className} ast-a-element`}
      {...targetProp}
      {...navigationProp}
      style={style}
    >
      {captionInserter()}
    </LinkPlainExternal>
  );
};

const hrefIsEmbedVideo = (href: string): boolean =>
  href?.includes('youtube.com/embed') || href?.includes('player.vimeo.com/video');

const hrefIsEmbedLinkedInPost = (href: string): boolean => href?.includes('linkedin.com/embed');

export const isEmbedInstagramPost = (element: MarkupElement): boolean =>
  element?.tagName === 'img' && element?.properties?.alt === 'Instagram';

export const isEmbedSenjaWidget = (element: MarkupElement): boolean =>
  element?.tagName === 'img' && element?.properties?.alt === 'Senja';

const splitElementValueString = (element: MarkupValue): string[] => element.value?.split('__').join('').split('\n');

const elementContainsEmbedComponent = (element: MarkupValue | MarkupElement): boolean => {
  if (element.type !== 'text') {
    if (element.tagName === 'a') {
      const href = element.properties?.href;

      if (hrefIsEmbedVideo(href)) {
        return true;
      }
    }

    if (isEmbedInstagramPost(element) || isEmbedSenjaWidget(element)) {
      return true;
    }

    return !!(element as MarkupElement).children.find((chunk) => elementContainsEmbedComponent(chunk));
  }

  return !!splitElementValueString(element).find(
    (chunk) => !!chunk?.match(/\(\(([toc)]+)\)\)/g) || !!chunk?.match(/\(\(([0-9)]+)\)\)/g)
  );
};

interface FormatElementFromHtmlAst {
  element: MarkupElement | MarkupValue;
  key: string;
  attachmentList?: Attachments;
  template?: Template;
}

// Returns a formatted HTML element related to the HtmlAst from contentful posts
export const formatElementFromHtmlAst = ({
  element,
  key,
  attachmentList = null,
  template = null,
}: FormatElementFromHtmlAst): JSX.Element => {
  const parseMarkupElementToEmbedComponent = (currentElement: MarkupElement): JSX.Element => {
    const childrenElements = currentElement.children;

    const { tocData } = useArticleTableOfContents();

    return (
      <React.Fragment key={key}>
        {childrenElements.map((chunkElement, index) => {
          if (chunkElement.type !== 'text') {
            if (chunkElement.tagName === 'a') {
              const href = chunkElement.properties?.href;

              if (hrefIsEmbedVideo(href)) {
                return (
                  <AttachmentContainer
                    key={`${key}-${index}`}
                    className='iframe-video-container'
                  >
                    <iframe
                      src={href}
                      frameBorder='0'
                      allowFullScreen
                      id={chunkElement.properties?.id}
                    />
                  </AttachmentContainer>
                );
              }
            }

            if (isEmbedInstagramPost(chunkElement)) {
              return (
                <InstagramPostWrapper
                  key={`${key}-${index}`}
                  dangerouslySetInnerHTML={{
                    __html: getEmbedInstagramPostHTML(chunkElement.properties.src),
                  }}
                />
              );
            }

            if (isEmbedSenjaWidget(chunkElement)) {
              return (
                <div
                  key={`${key}-${index}`}
                  className='senja-embed-container'
                >
                  <SenjaEmbed senjaId={chunkElement.properties.src} />
                </div>
              );
            }

            return (
              <p
                key={`${key}-${index}-p`}
                id={chunkElement.properties?.id}
                style={getStyleObjectFromString(chunkElement.properties.style)}
              >
                {mapRecursiveHtmlElement(chunkElement, key, attachmentList, template)}
              </p>
            );
          }

          if (chunkElement.value?.match(/\(\(([0-9)]+)\)\)/g) && attachmentList?.length) {
            return mapMarkdownAttachments((chunkElement as MarkupValue).value, key, attachmentList);
          }

          if (chunkElement.value?.match(/\(\(([toc)]+)\)\)/g) && tocData?.length) {
            return mapTableOfContent((chunkElement as MarkupValue).value, key);
          }

          return (
            <p
              className='ast-p-element'
              key={`${key}-${index}-p`}
            >
              {formatElementFromHtmlAst({
                element: { type: 'text', value: chunkElement.value },
                key,
              })}
            </p>
          );
        })}
      </React.Fragment>
    );
  };

  const elementIsBreakLine = element.type === 'text' && element.value === '\n';

  // Fix multiple breaklines before table (Unknown bug, from contentful maybe?)
  if (element.type === 'text' && element.value.indexOf('\n\n\n\n\n\n\n') !== -1) {
    return null;
  }
  if (element.type === 'element') {
    // p initial tag can be either a normal paragraph or a button wrapper
    // >> button wrappers, always have the same construction, p -> stronmg -> a
    // >> This is because buttons don't exist in Markdown
    if (element.tagName === 'p') {
      if (element.children.length === 1 && (element.children[0] as MarkupElement).tagName === 'strong') {
        const child = element.children[0] as MarkupElement;
        if (child.children.length === 1 && (child.children[0] as MarkupElement).tagName === 'a') {
          const linkChild = child.children[0] as MarkupElement;
          return (
            <p
              className='ast-p-element center'
              key={key}
            >
              <HtmlAstAnchorElement
                url={linkChild.properties?.href}
                className='button'
              >
                {(linkChild.children[0] as MarkupValue).value}
              </HtmlAstAnchorElement>
            </p>
          );
        }
      } else if (
        element.children.length > 1 &&
        element.children.every((child) => (child as MarkupElement).tagName === 'img')
      ) {
        return (
          <TemplateImagePair key={key}>
            {element.children.map((child, childIndex) => (
              <TemplateImageCovered
                key={`${key}-${childIndex}`}
                style={{ backgroundImage: `url(${(child as MarkupElement)?.properties?.src})` }}
              />
            ))}
          </TemplateImagePair>
        );
      } else if (elementContainsEmbedComponent(element)) {
        return parseMarkupElementToEmbedComponent(element);
      }

      return (
        <p
          className='ast-p-element'
          key={key}
          id={element.properties?.id}
          style={getStyleObjectFromString(element.properties.style)}
        >
          {mapRecursiveHtmlElement(element, key, attachmentList, template)}
        </p>
      );
    } else if (element.tagName === 'a') {
      if (template === 'legalArticle') {
        return (
          <PolicyLink
            to={element.properties?.href}
            key={key}
          >
            {mapRecursiveHtmlElement(element, key, attachmentList, template)}
          </PolicyLink>
        );
      }

      return (
        <HtmlAstAnchorElement
          className='ast-a-element'
          key={key}
          url={element.properties?.href}
          style={getStyleObjectFromString(element.properties.style)}
        >
          {mapRecursiveHtmlElement(element, key, attachmentList)}
        </HtmlAstAnchorElement>
      );
    } else if (element.tagName === 'em') {
      return (
        <em
          className='ast-em-element'
          key={key}
          id={element.properties?.id}
          style={getStyleObjectFromString(element.properties.style)}
        >
          {element.children.map((child, index) => formatElementFromHtmlAst({ element: child, key: `${key}-${index}` }))}
        </em>
      );
    } else if (element.tagName === 'blockquote') {
      return (
        <TemplateBlockQuote
          key={key}
          className='ast-blockquote-element'
        >
          <TemplateBlockQuoteIcon
            xmlns='http://www.w3.org/2000/svg'
            width='60'
            height='44'
            viewBox='0 0 60 44'
            fill='none'
          >
            <path
              d='M28.4822 2.30828V1.89788L28.0797 1.81787C26.6473 1.53313 24.6675 1.3938 23.1171 1.3938C15.8497 1.3938 10.1752 3.62264 6.32118 7.74025C2.47181 11.8529 0.5 17.784 0.5 25.0713C0.5 31.2674 2.26746 35.8533 5.0476 38.8957C7.82853 41.9389 11.5837 43.3938 15.4664 43.3938C23.2427 43.3938 28.6223 37.9689 28.6223 30.6916C28.6223 23.4448 23.5475 17.9894 16.3015 17.9894C14.3723 17.9894 12.3782 18.43 10.8746 19.5096C11.3452 17.1435 12.4668 15.0146 14.1471 13.3578L14.1591 13.346L14.1702 13.3334C16.0037 11.2652 18.9174 9.93727 22.8369 9.93727C24.8824 9.93727 25.6442 10.0404 27.2135 10.2529C27.4305 10.2823 27.663 10.3138 27.9165 10.3474L28.4822 10.4224V9.85174V2.30828ZM59.2198 2.30828V1.90635L58.8272 1.81996C57.5216 1.53261 55.5362 1.3938 53.9892 1.3938C46.7245 1.3938 41.0514 3.62268 37.1981 7.74029C33.3495 11.8529 31.3777 17.784 31.3777 25.0713C31.3777 31.2701 33.1817 35.8557 35.9789 38.8969C38.7759 41.9378 42.5297 43.3938 46.344 43.3938C53.9765 43.3938 59.5 37.9726 59.5 30.6916C59.5 23.445 54.4309 17.9894 47.1792 17.9894C45.2917 17.9894 43.2276 18.4124 41.75 19.4713C42.1952 17.1099 43.2207 14.9984 44.8847 13.3578L44.8905 13.3521L44.8961 13.3462C46.8778 11.2609 49.8025 9.93727 53.5744 9.93727C55.7983 9.93727 56.5574 10.0464 58.1162 10.2705C58.2838 10.2946 58.4607 10.32 58.6496 10.3468L59.2198 10.4276V9.85174V2.30828Z'
              stroke='#141921'
            />
          </TemplateBlockQuoteIcon>
          {element.children.map((child, index) => formatElementFromHtmlAst({ element: child, key: `${key}-${index}` }))}
        </TemplateBlockQuote>
      );
    } else if (element.tagName === 'h1') {
      return (
        <h1
          className='ast-h1-element'
          key={key}
          style={getStyleObjectFromString(element.properties.style)}
          id={element.properties?.id}
        >
          {mapRecursiveHtmlElement(element, key, attachmentList)}
        </h1>
      );
    } else if (element.tagName === 'h2') {
      return (
        <h2
          className='ast-h2-element'
          key={key}
          style={getStyleObjectFromString(element.properties.style)}
          id={element.properties?.id ?? headingToKey(getValueRecursive(element.children[0]))}
        >
          {mapRecursiveHtmlElement(element, key, attachmentList)}
        </h2>
      );
    } else if (element.tagName === 'h3') {
      return (
        <h3
          className='ast-h3-element'
          key={key}
          style={getStyleObjectFromString(element.properties.style)}
          id={element.properties?.id ?? headingToKey(getValueRecursive(element.children[0]))}
        >
          {mapRecursiveHtmlElement(element, key, attachmentList)}
        </h3>
      );
    } else if (element.tagName === 'h4') {
      return (
        <h4
          className='ast-h4-element'
          key={key}
          style={getStyleObjectFromString(element.properties.style)}
          id={element.properties?.id}
        >
          {mapRecursiveHtmlElement(element, key, attachmentList)}
        </h4>
      );
    } else if (element.tagName === 'h5') {
      return (
        <h5
          className='ast-h5-element'
          key={key}
          style={getStyleObjectFromString(element.properties.style)}
          id={element.properties?.id}
        >
          {mapRecursiveHtmlElement(element, key, attachmentList)}
        </h5>
      );
    } else if (element.tagName === 'strong') {
      return (
        <strong
          className='ast-strong-element'
          key={key}
          id={element.properties?.id}
          style={getStyleObjectFromString(element.properties.style)}
        >
          {mapRecursiveHtmlElement(element, key, attachmentList)}
        </strong>
      );
    } else if (element.tagName === 'ul') {
      return (
        <ul
          className='ast-ul-element'
          key={key}
          id={element.properties?.id}
          style={getStyleObjectFromString(element.properties.style)}
        >
          {mapRecursiveHtmlElement(element, key, attachmentList)}
        </ul>
      );
    } else if (element.tagName === 'ol') {
      return (
        <ol
          className='ast-ol-element'
          key={key}
          id={element.properties?.id}
          style={getStyleObjectFromString(element.properties.style)}
        >
          {mapRecursiveHtmlElement(element, key, attachmentList)}
        </ol>
      );
    } else if (element.tagName === 'li') {
      return (
        <li
          className='ast-li-element'
          key={key}
          id={element.properties?.id}
          style={getStyleObjectFromString(element.properties.style)}
        >
          <div>{mapRecursiveHtmlElement(element, key, attachmentList)}</div>
        </li>
      );
    } else if (element.tagName === 'img') {
      const shouldAddImageFooter = !!element.properties?.alt && ['caseStudyPro', 'resourcePro'].includes(template);

      return !shouldAddImageFooter ? (
        <img
          className='ast-img-element'
          key={key}
          src={element.properties.src}
          id={element.properties?.id}
          alt={element.properties?.alt}
        />
      ) : (
        <React.Fragment key={key}>
          <img
            className='ast-img-element'
            src={element.properties.src}
            id={element.properties?.id}
            alt={element.properties?.alt}
          />
          <span className='image-footer-description'>
            <span>{element.properties.alt}</span>
          </span>
        </React.Fragment>
      );
    } else if (element.tagName === 'table') {
      return (
        <table
          className='ast-table-element'
          id={element.properties?.id}
          style={getStyleObjectFromString(element.properties.style)}
        >
          {mapRecursiveHtmlElement(element, key, attachmentList)}
        </table>
      );
    } else if (element.tagName === 'thead') {
      return (
        <thead
          className='ast-thead-element'
          style={getStyleObjectFromString(element.properties.style)}
        >
          {mapRecursiveHtmlElement(element, key, attachmentList)}
        </thead>
      );
    } else if (element.tagName === 'tbody') {
      return (
        <tbody
          className='ast-tbody-element'
          style={getStyleObjectFromString(element.properties.style)}
        >
          {mapRecursiveHtmlElement(element, key, attachmentList)}
        </tbody>
      );
    } else if (element.tagName === 'tr') {
      return (
        <tr
          className='ast-tr-element'
          style={getStyleObjectFromString(element.properties.style)}
        >
          {mapRecursiveHtmlElement(element, key, attachmentList)}
        </tr>
      );
    } else if (element.tagName === 'th') {
      return (
        <th
          className='ast-th-element'
          style={getStyleObjectFromString(element.properties.style)}
        >
          {mapRecursiveHtmlElement(element, key, attachmentList)}
        </th>
      );
    } else if (element.tagName === 'td') {
      return (
        <td
          className='ast-td-element'
          style={getStyleObjectFromString(element.properties.style)}
        >
          {mapRecursiveHtmlElement(element, key, attachmentList)}
        </td>
      );
    } else if (element.tagName === 'iframe') {
      const src = element.properties.src;

      // Validates if the iframe points to LinkedIn
      if (hrefIsEmbedLinkedInPost(src)) {
        return renderLinkedInEmbeddedPost(element.properties);
      }

      return null;
    } else if (element.tagName === 'br' || elementIsBreakLine) {
      return (
        <br
          className='ast-br-element'
          key={`${key}-br`}
        />
      );
    } else if (element.tagName === 'hr') {
      return (
        <hr
          className='ast-hr-element'
          key={key}
        />
      );
    } else {
      return (
        <span
          key={key}
          style={getStyleObjectFromString(element.properties.style)}
        >
          {JSON.stringify(element)}
        </span>
      );
    }
  } else {
    return (
      <span key={key}>
        {splitElementValueString(element).map((chunk, index) => {
          return index > 0 ? (
            <React.Fragment key={`${key}--${index}`}>
              <TemplateSpacer />
              {chunk}
            </React.Fragment>
          ) : (
            <React.Fragment key={`${key}--${index}`}>{chunk}</React.Fragment>
          );
        })}
      </span>
    );
  }
};
